import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Typography,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
  Collapse,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Input,
  Button,
  CustomTimePicker,
  CustomDatePicker,
  TranslucentContainer,
  FeedbackError,
} from '../../../../../components';
import { onDisplayAlert } from '../../../../../utils/alerts/alerts';
import * as RetailProfileActions from '../../../../../core/retail-profiles/action';
import * as EventActions from '../../../../../core/events/action';
import { VENUE_STATUS } from '../../../../../constants/venue-status';
import RetailProfileAlert from './RetailProfileAlert';
import { buildCreateRetailProfileSchema } from './CreateRetailProfileSchema';
import { useScrollToFirstError } from '../../../../../hooks/useScrollToFirstError';
import './CreateRetailProfile.scss';

/**
 * Submit-time validation feedback for the retail profile form: on a failed
 * submit, shows a "missing information" toast and scrolls to the first invalid
 * field so its highlight is visible.
 */
function RetailProfileErrorFocus({ submitCount, errors, isSubmitting }) {
  useScrollToFirstError({ submitCount, errors, isSubmitting });
  return null;
}

/* eslint-disable react/forbid-prop-types */
RetailProfileErrorFocus.propTypes = {
  submitCount: PropTypes.number.isRequired,
  errors: PropTypes.object.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};
/* eslint-enable react/forbid-prop-types */

const DAYS_OF_WEEK = [
  { id: 'monday', label: 'Monday' },
  { id: 'tuesday', label: 'Tuesday' },
  { id: 'wednesday', label: 'Wednesday' },
  { id: 'thursday', label: 'Thursday' },
  { id: 'friday', label: 'Friday' },
  { id: 'saturday', label: 'Saturday' },
  { id: 'sunday', label: 'Sunday' },
];

function CreateRetailProfile() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentVenue } = useSelector((store) => store.venues);
  const { loading, currentProfile, profiles } = useSelector((store) => store.retailProfiles);

  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [pendingSchedules, setPendingSchedules] = useState(null);
  const profilePromiseRef = useRef(null);

  const [activeDays, setActiveDays] = useState({
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  });

  const initialValues = {
    start_date: '',
    end_date: '',
    monday: {
      start_time: '',
      end_time: '',
      price_per_hour: '',
      slots_available: '',
      space_dimensions: '',
      space_type: 'indoor',
    },
    tuesday: {
      start_time: '',
      end_time: '',
      price_per_hour: '',
      slots_available: '',
      space_dimensions: '',
      space_type: 'indoor',
    },
    wednesday: {
      start_time: '',
      end_time: '',
      price_per_hour: '',
      slots_available: '',
      space_dimensions: '',
      space_type: 'indoor',
    },
    thursday: {
      start_time: '',
      end_time: '',
      price_per_hour: '',
      slots_available: '',
      space_dimensions: '',
      space_type: 'indoor',
    },
    friday: {
      start_time: '',
      end_time: '',
      price_per_hour: '',
      slots_available: '',
      space_dimensions: '',
      space_type: 'indoor',
    },
    saturday: {
      start_time: '',
      end_time: '',
      price_per_hour: '',
      slots_available: '',
      space_dimensions: '',
      space_type: 'indoor',
    },
    sunday: {
      start_time: '',
      end_time: '',
      price_per_hour: '',
      slots_available: '',
      space_dimensions: '',
      space_type: 'indoor',
    },
  };

  // Clear retail profile state when component mounts
  useEffect(() => {
    dispatch(RetailProfileActions.clearRetailProfileState());
  }, [dispatch]);

  // Load existing retail profiles for the venue
  useEffect(() => {
    if (currentVenue?.id) {
      dispatch(RetailProfileActions.getRetailProfilesByVenue(currentVenue.id));
    }
  }, [dispatch, currentVenue]);

  // Check if venue already has an active retail profile
  const hasActiveProfile = profiles && profiles.some((profile) => profile.status === 'active');

  const handleDayToggle = (day) => {
    setActiveDays((prev) => ({
      ...prev,
      [day]: !prev[day],
    }));
  };

  // Watch for profile creation and create schedules
  useEffect(() => {
    if (currentProfile && currentProfile.id && pendingSchedules && pendingSchedules.length > 0) {
      // Profile was created, now create the schedules
      pendingSchedules.forEach((schedule) => {
        const scheduleWithProfileId = {
          ...schedule,
          retail_profile_id: currentProfile.id,
        };
        dispatch(RetailProfileActions.createRetailSchedule(scheduleWithProfileId));
      });

      // Clear pending schedules
      setPendingSchedules(null);

      // Navigate to Manage Retail Spaces view (tab 6) after schedules are created
      setTimeout(() => {
        onDisplayAlert({
          type: 'success',
          title: 'Success',
          description: 'Retail profile and schedules created successfully!',
        });
        dispatch(EventActions.onChangeTabEvent(6));
      }, 1500);
    }
  }, [currentProfile, pendingSchedules, dispatch]);

  const handlePreview = () => {
    setIsPreviewMode(true);
  };

  const handleBackToEdit = () => {
    setIsPreviewMode(false);
  };

  const canCreateRetailProfile = () =>
    currentVenue !== null && currentVenue?.status === VENUE_STATUS.APPROVED;

  const handleSubmit = async (values) => {
    if (!currentVenue || !currentVenue.id) {
      onDisplayAlert({
        title: 'Error',
        description: 'No venue selected. Please select a venue first.',
        type: 'error',
      });
      return;
    }

    // Check if venue is approved before allowing creation
    if (!canCreateRetailProfile()) {
      onDisplayAlert({
        title: 'Venue Not Approved',
        description: 'Your venue must be approved before creating retail profiles.',
        type: 'warning',
      });
      return;
    }

    // Step 1: Create the retail profile
    const profileData = {
      venue_id: currentVenue.id,
      status: 'active',
      start_date: values.start_date || null,
      end_date: values.end_date || null,
    };

    // Step 2: Prepare schedules for each active day (without profile ID yet)
    const activeSchedules = Object.keys(activeDays)
      .filter((day) => activeDays[day])
      .map((day) => ({
        day_of_week: day,
        start_time: values[day].start_time,
        end_time: values[day].end_time,
        price_per_hour: parseFloat(values[day].price_per_hour),
        slots_available: parseInt(values[day].slots_available, 10),
        space_dimensions: values[day].space_dimensions || null,
        space_type: values[day].space_type,
        is_active: true,
      }));

    // Store schedules to be created after profile creation
    setPendingSchedules(activeSchedules);

    // Dispatch profile creation - useEffect will handle schedules after profile is created
    dispatch(RetailProfileActions.createRetailProfile(profileData));
  };

  const getSpaceTypeLabel = (type) => {
    const labels = {
      indoor: 'Indoor',
      outdoor: 'Outdoor',
      both: 'Indoor & Outdoor',
    };
    return labels[type] || type;
  };

  const renderDayFields = (day, values, errors, touched, setFieldValue) => (
    <Collapse in={activeDays[day]} timeout="auto" unmountOnExit>
      <Paper elevation={2} sx={{ p: 3, mt: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
        <Grid container spacing={3}>
          {/* Time Fields */}
          <Grid item xs={12} sm={6}>
            <CustomTimePicker
              label="Start Time"
              name={`${day}.start_time`}
              value={values[day].start_time}
              setFieldValue={setFieldValue}
            />
            <FeedbackError name={`${day}.start_time`} errors={errors} touched={touched} />
          </Grid>

          <Grid item xs={12} sm={6}>
            <CustomTimePicker
              label="End Time"
              name={`${day}.end_time`}
              value={values[day].end_time}
              setFieldValue={setFieldValue}
            />
            <FeedbackError name={`${day}.end_time`} errors={errors} touched={touched} />
          </Grid>

          {/* Price and Slots */}
          <Grid item xs={12} sm={5}>
            <Typography variant="caption" display="block" marginBottom={1}>
              Price Per Hour ($)
            </Typography>
            <Input
              name={`${day}.price_per_hour`}
              type="number"
              placeholder="e.g., 30.00"
              value={values[day].price_per_hour}
              onChange={(e) => setFieldValue(`${day}.price_per_hour`, e.target.value)}
              fullWidth
            />
            <FeedbackError name={`${day}.price_per_hour`} errors={errors} touched={touched} />
          </Grid>

          {/* Spacer */}
          <Grid item xs={0} sm={1} />

          <Grid item xs={12} sm={5}>
            <Typography variant="caption" display="block" marginBottom={1}>
              Slots Available
            </Typography>
            <Input
              name={`${day}.slots_available`}
              type="number"
              placeholder="e.g., 5"
              value={values[day].slots_available}
              onChange={(e) => setFieldValue(`${day}.slots_available`, e.target.value)}
              fullWidth
            />
            <FeedbackError name={`${day}.slots_available`} errors={errors} touched={touched} />
          </Grid>

          {/* Space Dimensions */}
          <Grid item xs={12} sm={11}>
            <Typography variant="caption" display="block" marginBottom={1}>
              Space Dimensions
            </Typography>
            <Input
              name={`${day}.space_dimensions`}
              placeholder="e.g., 10x10 ft or 100 sq ft"
              value={values[day].space_dimensions}
              onChange={(e) => setFieldValue(`${day}.space_dimensions`, e.target.value)}
              fullWidth
            />
            <FeedbackError name={`${day}.space_dimensions`} errors={errors} touched={touched} />
          </Grid>

          {/* Indoor/Outdoor Toggle */}
          <Grid item xs={12}>
            <Typography variant="caption" display="block" marginBottom={1}>
              Space Type
            </Typography>
            <ToggleButtonGroup
              value={values[day].space_type}
              exclusive
              onChange={(e, newValue) => {
                if (newValue !== null) {
                  setFieldValue(`${day}.space_type`, newValue);
                }
              }}
              aria-label="space type"
              sx={{
                '& .MuiToggleButton-root': {
                  borderRadius: '25px',
                  margin: '0 4px',
                  padding: '8px 20px',
                  textTransform: 'none',
                  fontWeight: '500',
                  '&.Mui-selected': {
                    backgroundColor: '#EE7B57',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#d86a46',
                    },
                  },
                },
              }}>
              <ToggleButton value="indoor" aria-label="indoor">
                Indoor
              </ToggleButton>
              <ToggleButton value="outdoor" aria-label="outdoor">
                Outdoor
              </ToggleButton>
              <ToggleButton value="both" aria-label="both">
                Both
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
        </Grid>
      </Paper>
    </Collapse>
  );

  const renderPreview = (values) => {
    const activeDaysData = DAYS_OF_WEEK.filter((day) => activeDays[day.id]).map((day) => ({
      ...day,
      data: values[day.id],
    }));

    return (
      <Grid container justifyContent="center" marginTop={4}>
        <Grid item xs={12} md={10} lg={8}>
          <TranslucentContainer>
            <Box sx={{ p: 4 }}>
              <Typography variant="h4" fontWeight="bold" color="#EE7B57" marginBottom={2}>
                Review Retail Profile
              </Typography>
              <Typography variant="body1" marginBottom={4} color="text.secondary">
                Please review your retail profile details before saving.
              </Typography>

              {/* Duration Display */}
              {(values.start_date || values.end_date) && (
                <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: '#fff3e0' }}>
                  <Typography variant="h6" fontWeight="bold" color="#333" marginBottom={2}>
                    Profile Duration
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Start Date
                      </Typography>
                      <Typography variant="body1" fontWeight="600">
                        {values.start_date ? values.start_date.format('MMMM DD, YYYY') : 'Not set'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        End Date
                      </Typography>
                      <Typography variant="body1" fontWeight="600">
                        {values.end_date ? values.end_date.format('MMMM DD, YYYY') : 'Not set'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {activeDaysData.length === 0 ? (
                <Typography variant="body1" color="text.secondary" textAlign="center">
                  No days have been configured.
                </Typography>
              ) : (
                activeDaysData.map((dayInfo) => (
                  <Paper
                    key={dayInfo.id}
                    elevation={3}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderLeft: '4px solid #EE7B57',
                    }}>
                    <Typography variant="h6" fontWeight="bold" color="#333" marginBottom={2}>
                      {dayInfo.label}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Operating Hours
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {dayInfo.data.start_time || 'Not set'} -{' '}
                          {dayInfo.data.end_time || 'Not set'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Space Type
                        </Typography>
                        <Chip
                          label={getSpaceTypeLabel(dayInfo.data.space_type)}
                          size="small"
                          sx={{
                            backgroundColor: '#EE7B57',
                            color: 'white',
                            fontWeight: '600',
                            mt: 0.5,
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Price Per Hour
                        </Typography>
                        <Typography variant="h6" fontWeight="bold" color="#EE7B57">
                          ${dayInfo.data.price_per_hour || '0.00'}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Slots Available
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {dayInfo.data.slots_available || '0'} slots
                        </Typography>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Space Dimensions
                        </Typography>
                        <Typography variant="body1" fontWeight="600">
                          {dayInfo.data.space_dimensions || 'Not specified'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}

              {/* Action Buttons - Only show if venue is approved */}
              {canCreateRetailProfile() && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                  <Button type="button" label="Back to Edit" onClick={handleBackToEdit} outline />
                  <Button
                    type="button"
                    label="Save Retail Profile"
                    onClick={() => handleSubmit(values)}
                    disabled={loading}
                  />
                </Box>
              )}
            </Box>
          </TranslucentContainer>
        </Grid>
      </Grid>
    );
  };

  // If venue already has an active profile, show message instead of form
  if (hasActiveProfile) {
    return (
      <Box className="create-retail-profile">
        <Grid container justifyContent="center" marginTop={4}>
          <Grid item xs={12} md={8} lg={6}>
            <TranslucentContainer>
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="#EE7B57" marginBottom={2}>
                  Retail Profile Already Exists
                </Typography>
                <Typography variant="body1" marginBottom={3} color="text.secondary">
                  Your venue already has an active retail profile. You can only have one active
                  retail profile at a time.
                </Typography>
                <Typography variant="body2" marginBottom={4} color="text.secondary">
                  To create a new retail profile, please deactivate or delete your existing profile
                  first from the Manage Retail Spaces section.
                </Typography>
                <Button
                  type="button"
                  label="Go to Manage Retail Spaces"
                  onClick={() => dispatch(EventActions.onChangeTabEvent(6))}
                />
              </Box>
            </TranslucentContainer>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box className="create-retail-profile">
      <Formik
        initialValues={initialValues}
        validationSchema={buildCreateRetailProfileSchema(activeDays)}
        onSubmit={(values) => {
          // Prevent submission if venue is not approved
          if (!canCreateRetailProfile()) {
            onDisplayAlert({
              title: 'Venue Not Approved',
              description: 'Your venue must be approved before creating retail profiles.',
              type: 'warning',
            });
            return;
          }
          handleSubmit(values);
        }}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize>
        {({ values, errors, touched, setFieldValue, isSubmitting, submitCount }) => (
          <Form
            onSubmit={(e) => {
              // Prevent form submission if venue is not approved
              if (!canCreateRetailProfile()) {
                e.preventDefault();
                onDisplayAlert({
                  title: 'Venue Not Approved',
                  description: 'Your venue must be approved before creating retail profiles.',
                  type: 'warning',
                });
              }
            }}>
            <RetailProfileErrorFocus
              submitCount={submitCount}
              errors={errors}
              isSubmitting={isSubmitting}
            />
            {isPreviewMode ? (
              renderPreview(values)
            ) : (
              <Grid container justifyContent="center" marginTop={4}>
                <Grid item xs={12} md={10} lg={8}>
                  <TranslucentContainer>
                    <Box sx={{ p: 4 }}>
                      <Typography variant="h4" fontWeight="bold" color="#EE7B57" marginBottom={2}>
                        Create Retail Profile
                      </Typography>
                      <Typography variant="body1" marginBottom={4} color="text.secondary">
                        Configure your retail space availability by day. Toggle each day on to set
                        specific hours, pricing, and space details.
                      </Typography>

                      {/* Approval Alert */}
                      {!canCreateRetailProfile() && (
                        <Box sx={{ mb: 4 }}>
                          <RetailProfileAlert
                            isApproved={currentVenue?.status === VENUE_STATUS.APPROVED}
                          />
                        </Box>
                      )}

                      {/* Duration Section */}
                      <Paper elevation={2} sx={{ p: 3, mb: 4, backgroundColor: '#f9f9f9' }}>
                        <Typography variant="h6" fontWeight="600" color="#333" marginBottom={2}>
                          Profile Duration
                        </Typography>
                        <Typography variant="body2" color="text.secondary" marginBottom={3}>
                          Set the date range when this retail profile will be active. Leave blank
                          for indefinite availability.
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="caption" display="block" marginBottom={1}>
                              Start Date
                            </Typography>
                            <CustomDatePicker
                              values={values}
                              errors={errors}
                              touched={touched}
                              setFieldValue={setFieldValue}
                              name="start_date"
                            />
                          </Grid>
                          <Grid item xs={12} sm={3}>
                            <Typography variant="caption" display="block" marginBottom={1}>
                              End Date
                            </Typography>
                            <CustomDatePicker
                              values={values}
                              errors={errors}
                              touched={touched}
                              setFieldValue={setFieldValue}
                              name="end_date"
                              minDate={values.start_date || undefined}
                            />
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* Days of the Week */}
                      {DAYS_OF_WEEK.map((day) => (
                        <Box key={day.id} className="day-section">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1,
                            }}>
                            <Typography variant="h6" fontWeight="600" color="#333">
                              {day.label}
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={activeDays[day.id]}
                                  onChange={() => handleDayToggle(day.id)}
                                  sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                      color: '#EE7B57',
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                      backgroundColor: '#EE7B57',
                                    },
                                  }}
                                />
                              }
                              label={activeDays[day.id] ? 'Available' : 'Closed'}
                              labelPlacement="start"
                            />
                          </Box>

                          {renderDayFields(day.id, values, errors, touched, setFieldValue)}
                        </Box>
                      ))}

                      {/* Preview Button - Only show if venue is approved */}
                      {canCreateRetailProfile() && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                          <Button
                            type="button"
                            label="Preview"
                            onClick={handlePreview}
                            disabled={
                              isSubmitting ||
                              loading ||
                              !Object.values(activeDays).some((day) => day === true)
                            }
                          />
                        </Box>
                      )}

                      {!Object.values(activeDays).some((day) => day === true) && (
                        <Typography
                          variant="caption"
                          display="block"
                          textAlign="center"
                          color="text.secondary"
                          marginTop={2}>
                          Please enable at least one day to preview the profile
                        </Typography>
                      )}
                    </Box>
                  </TranslucentContainer>
                </Grid>
              </Grid>
            )}
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default CreateRetailProfile;
